/**
 * Firebase Error Handling Utility
 * Provides comprehensive error management for Firebase operations
 * 
 * IMPORTANT: This module has been completely refactored to avoid class static property issues
 * that were causing production errors with ERROR_CODES access.
 * 
 * Note: We also check for window.GIGIFY_ERROR_CODES which is set in index.html as a fallback.
 */

// Import the debug utility
import debug from '../../../utils/debug'; // Path may need adjustment based on file location

// Try to use the global error codes first (from index.html), or default to our local version
// This provides a fallback in case class properties get optimized away in production
const ERROR_CODES = (typeof window !== 'undefined' && window.GIGIFY_ERROR_CODES) ? 
  window.GIGIFY_ERROR_CODES : 
  {
    "auth/invalid-email": "Invalid email address format",
    "auth/user-disabled": "This user account has been disabled",
    "auth/user-not-found": "No account found with this email",
    "auth/wrong-password": "Incorrect password",
    "auth/email-already-in-use": "Email is already registered",
    "auth/operation-not-allowed": "This operation is not permitted",
    "auth/popup-closed-by-user": "Google sign-in was cancelled. Please try again",
    "auth/cancelled-popup-request": "Multiple popup requests were detected",
    "auth/popup-blocked": "The popup was blocked by the browser",
    "auth/requires-recent-login": "Please login again to complete this operation",
    "auth/google-signin-failed": "Google sign-in failed. Please try email login instead.",
    "permission-denied": "You don't have permission to perform this action",
    "not-found": "The requested resource was not found",
    "already-exists": "This resource already exists",
    "invalid-argument": "Invalid information provided",
    "unauthenticated": "You must be logged in to perform this action",
    "resource-exhausted": "Service temporarily unavailable. Please try again later",
    "cancelled": "Operation was cancelled",
    "network-error": "Network connection failed",
    "timeout": "Operation timed out",
    "internal-error": "An unexpected server error occurred"
  };

/**
 * Handle Firebase errors and return a standardized error response
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 * @returns {Object} Standardized error response
 */
function handleFirebaseError(error, context = "Firebase operation") {
  // Safety check - make sure ERROR_CODES is available
  if (!ERROR_CODES && typeof window !== 'undefined' && window.GIGIFY_ERROR_CODES) {
    console.log('Using fallback ERROR_CODES from window.GIGIFY_ERROR_CODES');
    Object.assign(ERROR_CODES, window.GIGIFY_ERROR_CODES);
  }
  
  try {
    // Determine error code from error object or message patterns
    const errorCode = error?.code || 
      (error?.message?.includes("permission-denied") ? "permission-denied" : 
      (error?.message?.includes("network") ? "network-error" : "UNKNOWN_ERROR"));

    // Get user-friendly message from error codes map or use original message
    // Use a try-catch here to handle potential "Cannot read properties of undefined" errors
    let userMessage;
    try {
      userMessage = ERROR_CODES[errorCode] || error?.message || "An unexpected error occurred";
    } catch (err) {
      console.error('Error accessing ERROR_CODES, falling back to direct message', err);
      // Fallback to direct message
      userMessage = error?.message || "An unexpected error occurred";
    }

    // Log the error using debug utility
    try {
      debug.error('FirebaseErrorHandler', `${context}: ${userMessage}`, {
        code: errorCode,
        originalError: error
      });
    } catch (debugError) {
      console.error('Error using debug utility:', debugError);
    }

    // Return standardized error response
    return {
      success: false,
      error: userMessage,
      code: errorCode,
      context,
      details: process.env.NODE_ENV === 'development' 
        ? { message: error?.message, stack: error?.stack } 
        : undefined
    };
  } catch (handlerError) {
    // If error handler itself fails, log and return a fallback
    console.error("Error in Firebase error handler:", handlerError);
    return {
      success: false,
      error: error?.message || "An error occurred while processing the error",
      code: "error-handler-failure",
      context
    };
  }
}

/**
 * Create a standardized Firebase error
 * @param {string} code - Error code
 * @param {string} message - Error message
 * @param {Object} details - Additional error details
 * @returns {Error} Error object with code and details
 */
function createFirebaseError(code, message, details = {}) {
  const error = new Error(message);
  error.code = code;
  error.details = details;
  
  // Log the created error
  debug.log('FirebaseErrorHandler', `Created error: ${code}`, { message, details });
  
  return error;
}

/**
 * Log an error to the monitoring system
 * @param {Error} error - The error to log
 * @param {string} level - Log level
 * @param {string} component - Component that generated the error
 */
function logToMonitoring(error, level = 'error', component = 'FirebaseErrorHandler') {
  debug[level](component, 'Monitored Error:', error);
}

/**
 * Log Firebase-specific operations
 * @param {string} service - Firebase service name
 * @param {string} operation - Operation being performed
 * @param {Object} data - Additional operation data
 */
function logFirebaseOperation(service, operation, data = {}) {
  debug.firebase(service, operation, data);
}

/**
 * Validate user UID parameter
 * @param {string} userUID - User ID to validate
 * @returns {boolean} Whether the userUID is valid
 */
function validateUserUID(userUID) {
  if (!userUID) {
    const error = createFirebaseError(
      'invalid-argument', 
      'Missing required userUID parameter',
      { field: 'userUID' }
    );
    handleFirebaseError(error, 'User validation');
    return false;
  }
  return true;
}

// Export all functions and constants
export {
  ERROR_CODES,
  handleFirebaseError,
  createFirebaseError,
  logToMonitoring,
  logFirebaseOperation,
  validateUserUID
};

// Export handleFirebaseError as default for backward compatibility
export default handleFirebaseError;
